/**
 * Legal progress tracker — LOI & Agreement client detail sections.
 * Stages come from Settings → Legal (admin-managed, ordered 1…n).
 */
import { useMemo, useState } from 'react';
import {
    ChevronDown, ChevronUp, FileCheck2, FileSignature, History,
    Loader2, Check, Circle, ArrowRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const WORKFLOWS = {
    loi: {
        title: 'LOI Progress',
        trackerLabel: 'LOI progress tracker',
        historyTitle: 'LOI stage history',
        updateTitle: 'Update LOI stage',
        successToast: 'LOI stage updated',
        apiSegment: 'loi-stage',
        currentOrderKey: 'loiCurrentStageOrder',
        historyKey: 'loiStageHistory',
        Icon: FileCheck2,
    },
    agreement: {
        title: 'Agreement Progress',
        trackerLabel: 'Agreement progress tracker',
        historyTitle: 'Agreement stage history',
        updateTitle: 'Update Agreement Stage',
        successToast: 'Agreement stage updated',
        apiSegment: 'agreement-stage',
        currentOrderKey: 'agreementCurrentStageOrder',
        historyKey: 'agreementStageHistory',
        Icon: FileSignature,
    },
};

const fd = (d) => {
    if (!d) return '—';
    const p = new Date(d);
    return isNaN(p) ? '—' : p.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

function stageState(i, currentIdx) {
    if (currentIdx < 0) return 'pending';
    if (i < currentIdx) return 'done';
    if (i === currentIdx) return 'current';
    return 'pending';
}

/** Accepted / Rejected are alternate branches — show one, not both. */
function isAcceptedStage(stage) {
    return /^accepted$/i.test(String(stage?.label || '').trim());
}

function isRejectedStage(stage) {
    return /^rejected$/i.test(String(stage?.label || '').trim());
}

function visibleLegalStages(stages, currentOrder) {
    const accepted = stages.find(isAcceptedStage);
    const rejected = stages.find(isRejectedStage);
    if (!accepted || !rejected) return stages;

    const onRejectedPath = currentOrder === rejected.order;
    return stages.filter(st => {
        if (onRejectedPath && st.order === accepted.order) return false;
        if (!onRejectedPath && st.order === rejected.order) return false;
        return true;
    });
}

export default function LoiProgressSection({
    variant = 'loi',
    submission,
    stages: stagesProp,
    loiStages,
    canUpdate = false,
    onUpdated,
}) {
    const cfg = WORKFLOWS[variant] || WORKFLOWS.loi;
    const HeaderIcon = cfg.Icon;
    const stagesInput = stagesProp ?? loiStages ?? [];

    const [open, setOpen] = useState(true);
    const [showHistory, setShowHistory] = useState(false);
    const [historyStageOrder, setHistoryStageOrder] = useState(null);
    const [showUpdate, setShowUpdate] = useState(false);
    const [busy, setBusy] = useState(false);
    const [pickOrder, setPickOrder] = useState('');
    const [notes, setNotes] = useState('');

    const stages = useMemo(
        () => [...stagesInput].filter(s => s.active !== false).sort((a, b) => a.order - b.order),
        [stagesInput],
    );

    const currentOrder = submission?.[cfg.currentOrderKey] ?? stages[0]?.order ?? 1;

    const displayStages = useMemo(
        () => visibleLegalStages(stages, currentOrder),
        [stages, currentOrder],
    );

    const currentIdx = displayStages.findIndex(s => s.order === currentOrder);
    const currentStage = displayStages[currentIdx] ?? stages.find(s => s.order === currentOrder) ?? stages[0];
    const total = displayStages.length;
    const stepNum = currentIdx >= 0 ? currentIdx + 1 : 1;
    const progressPct = total > 0 ? Math.round((stepNum / total) * 100) : 0;
    const doneCount = currentIdx >= 0 ? currentIdx : 0;

    const allHistory = useMemo(
        () => [...(submission?.[cfg.historyKey] || [])].reverse(),
        [submission, cfg.historyKey],
    );

    const filteredHistory = useMemo(() => {
        if (historyStageOrder == null) return allHistory;
        return allHistory.filter(h => h.stageOrder === historyStageOrder);
    }, [allHistory, historyStageOrder]);

    const historyStage = historyStageOrder != null
        ? stages.find(s => s.order === historyStageOrder)
        : null;

    const openAllHistory = () => {
        setHistoryStageOrder(null);
        setShowHistory(true);
    };

    const openStageHistory = (order) => {
        setHistoryStageOrder(order);
        setShowHistory(true);
    };

    const closeHistory = () => {
        setShowHistory(false);
        setHistoryStageOrder(null);
    };

    const openUpdate = () => {
        setPickOrder(String(currentOrder));
        setNotes('');
        setShowUpdate(true);
    };

    const submitUpdate = async (e) => {
        e.preventDefault();
        const order = Number(pickOrder);
        if (!order) return toast.error('Select a stage');
        setBusy(true);
        try {
            const { data } = await api.patch(`/forms/submissions/${submission._id}/${cfg.apiSegment}`, {
                stageOrder: order,
                notes: notes.trim(),
            });
            onUpdated?.(data);
            toast.success(cfg.successToast);
            setShowUpdate(false);
        } catch { /* interceptor */ }
        finally { setBusy(false); }
    };

    if (!stages.length) return null;

    return (
        <>
            <section className="rounded-2xl border border-ink-200/85 overflow-hidden shadow-[0_4px_24px_rgba(15,23,42,0.06)] ring-1 ring-ink-100/75">
                {/* Header */}
                <button
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 bg-gradient-to-r from-slate-900 via-brand-teal-900 to-teal-700 text-white text-left border-b border-white/10"
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/10 ring-1 ring-white/20">
                            <HeaderIcon className="h-5 w-5 text-brand-yellow" />
                        </span>
                        <div className="min-w-0">
                            <p className="font-heading font-extrabold text-sm uppercase tracking-[0.12em]">{cfg.title}</p>
                            <p className="text-[11px] text-white/70 mt-0.5 truncate">{currentStage?.label || '—'}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <span className="hidden sm:inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-bold tabular-nums ring-1 ring-white/15">
                            {stepNum}/{total}
                        </span>
                        {open ? <ChevronUp className="h-5 w-5 opacity-80" /> : <ChevronDown className="h-5 w-5 opacity-80" />}
                    </div>
                </button>

                {open && (
                    <div className="bg-white">
                        {/* Status strip */}
                        <div className="px-5 sm:px-6 py-5 border-b border-ink-100 bg-gradient-to-br from-brand-teal-50/60 via-white to-white">
                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 mb-3">{cfg.trackerLabel}</p>
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                <div className="flex items-start gap-4 min-w-0">
                                    <div className="relative shrink-0">
                                        <svg className="h-[72px] w-[72px] -rotate-90" viewBox="0 0 72 72" aria-hidden>
                                            <circle cx="36" cy="36" r="30" fill="none" stroke="currentColor" strokeWidth="5" className="text-ink-100" />
                                            <circle
                                                cx="36" cy="36" r="30" fill="none" stroke="currentColor" strokeWidth="5"
                                                strokeLinecap="round"
                                                strokeDasharray={`${2 * Math.PI * 30}`}
                                                strokeDashoffset={`${2 * Math.PI * 30 * (1 - progressPct / 100)}`}
                                                className="text-brand-teal transition-all duration-700"
                                            />
                                        </svg>
                                        <span className="absolute inset-0 grid place-items-center font-heading text-lg font-black text-brand-teal-800 tabular-nums">
                                            {progressPct}%
                                        </span>
                                    </div>
                                    <div className="min-w-0 pt-1">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500">Current stage</p>
                                        <p className="mt-1 font-heading text-xl sm:text-2xl font-extrabold text-ink-900 leading-tight">
                                            {currentStage?.label || '—'}
                                        </p>
                                        <p className="mt-1.5 text-sm text-ink-500">
                                            Step <span className="font-bold text-brand-teal-700">{stepNum}</span> of {total}
                                            {doneCount > 0 && (
                                                <span className="text-ink-400"> · {doneCount} completed</span>
                                            )}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex flex-wrap items-center gap-2 lg:justify-end shrink-0">
                                    <button
                                        type="button"
                                        onClick={openAllHistory}
                                        className="inline-flex items-center gap-2 rounded-xl border border-ink-200 bg-white px-4 py-2.5 text-sm font-semibold text-ink-700 hover:border-brand-teal/40 hover:text-brand-teal-700 transition shadow-sm"
                                    >
                                        <History className="h-4 w-4" />
                                        View history
                                    </button>
                                    {canUpdate && (
                                        <button
                                            type="button"
                                            onClick={openUpdate}
                                            className="inline-flex items-center gap-2 rounded-xl bg-brand-teal px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-brand-teal/20 hover:bg-brand-teal-700 transition"
                                        >
                                            Update stage
                                            <ArrowRight className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Segmented track */}
                            <div className="mt-5 flex gap-1">
                                {displayStages.map((st, i) => {
                                    const state = stageState(i, currentIdx);
                                    return (
                                        <div
                                            key={st.order}
                                            title={st.label}
                                            className={`h-1.5 flex-1 rounded-full transition-colors duration-500 ${
                                                state === 'done'
                                                    ? 'bg-brand-teal'
                                                    : state === 'current'
                                                        ? 'bg-gradient-to-r from-brand-teal to-brand-teal-300'
                                                        : 'bg-ink-100'
                                            }`}
                                        />
                                    );
                                })}
                            </div>
                        </div>

                        {/* All stages — wrapping grid, no scroll */}
                        <div className="px-5 sm:px-6 py-5">
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                                {displayStages.map((st, i) => {
                                    const state = stageState(i, currentIdx);
                                    return (
                                        <StageTile
                                            key={st.order}
                                            stage={st}
                                            state={state}
                                            onClick={() => openStageHistory(st.order)}
                                        />
                                    );
                                })}
                            </div>

                            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-ink-100">
                                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-medium text-ink-500">
                                    <span className="inline-flex items-center gap-2">
                                        <span className="grid h-5 w-5 place-items-center rounded-md bg-brand-teal text-white shadow-sm shadow-brand-teal/30">
                                            <span className="h-1.5 w-1.5 rounded-full bg-brand-yellow" />
                                        </span>
                                        Current
                                    </span>
                                    <span className="inline-flex items-center gap-2">
                                        <span className="grid h-5 w-5 place-items-center rounded-md bg-brand-teal-50 text-brand-teal ring-1 ring-brand-teal/25">
                                            <Check className="h-3 w-3" />
                                        </span>
                                        Completed
                                    </span>
                                    <span className="inline-flex items-center gap-2">
                                        <span className="grid h-5 w-5 place-items-center rounded-md bg-ink-50 text-ink-300 ring-1 ring-ink-200">
                                            <Circle className="h-2.5 w-2.5" />
                                        </span>
                                        Pending
                                    </span>
                                </div>
                                <p className="text-xs font-bold text-ink-600 tabular-nums">
                                    {stepNum} of {total} stages · {progressPct}% complete
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </section>

            {showHistory && (
                <ModalShell
                    title={historyStageOrder != null
                        ? `Stage ${historyStageOrder}: ${historyStage?.label || 'History'}`
                        : cfg.historyTitle}
                    onClose={closeHistory}
                >
                    {filteredHistory.length === 0 ? (
                        <div className="py-8 text-center space-y-2">
                            <p className="text-sm text-ink-500">
                                {historyStageOrder != null
                                    ? 'No updates recorded for this stage yet.'
                                    : 'No stage changes recorded yet.'}
                            </p>
                            {historyStageOrder != null && historyStageOrder > currentOrder && (
                                <p className="text-xs text-ink-400">This stage has not been reached.</p>
                            )}
                        </div>
                    ) : (
                        <ul className="divide-y divide-ink-100 max-h-[min(60vh,420px)] overflow-y-auto -mx-1">
                            {filteredHistory.map((h, i) => (
                                <HistoryEntry key={i} entry={h} stages={stages} />
                            ))}
                        </ul>
                    )}
                    {historyStageOrder != null && allHistory.length > 0 && (
                        <button
                            type="button"
                            onClick={() => setHistoryStageOrder(null)}
                            className="mt-4 w-full text-center text-xs font-semibold text-brand-teal-700 hover:text-brand-teal-900"
                        >
                            View full timeline
                        </button>
                    )}
                </ModalShell>
            )}

            {showUpdate && (
                <ModalShell title={cfg.updateTitle} onClose={() => !busy && setShowUpdate(false)}>
                    <form onSubmit={submitUpdate} className="space-y-4">
                        <div className="rounded-xl bg-brand-teal-50/80 border border-brand-teal/15 px-4 py-3 text-sm">
                            <span className="text-ink-500">Moving from </span>
                            <span className="font-bold text-brand-teal-800">{currentStage?.label}</span>
                        </div>
                        <div>
                            <label className="ag-label">Select new stage</label>
                            <select
                                className="ag-input"
                                value={pickOrder}
                                onChange={e => setPickOrder(e.target.value)}
                                required
                            >
                                {stages.map(st => (
                                    <option key={st.order} value={st.order}>
                                        {st.order}. {st.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="ag-label">Notes</label>
                            <textarea
                                className="ag-input min-h-[88px] resize-y"
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                placeholder="Add any notes about this stage change…"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2 border-t border-ink-100">
                            <button type="button" className="ag-btn-ghost" disabled={busy} onClick={() => setShowUpdate(false)}>Cancel</button>
                            <button type="submit" className="ag-btn-primary" disabled={busy}>
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                {busy ? 'Updating…' : 'Update stage'}
                            </button>
                        </div>
                    </form>
                </ModalShell>
            )}
        </>
    );
}

function HistoryEntry({ entry, stages }) {
    const fromStage = stages.find(s => s.order === entry.fromOrder);
    return (
        <li className="py-4 flex gap-3 px-1">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-teal-50 text-brand-teal-700 ring-1 ring-brand-teal/15">
                <span className="text-xs font-black tabular-nums">{entry.stageOrder}</span>
            </span>
            <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-ink-900">{entry.stageLabel || `Stage ${entry.stageOrder}`}</p>
                {fromStage && entry.fromOrder !== entry.stageOrder && (
                    <p className="text-[11px] text-ink-500 mt-0.5">
                        From {fromStage.label}
                    </p>
                )}
                <p className="text-xs text-ink-500 mt-0.5">{fd(entry.updatedAt)} · {entry.updatedBy?.name || 'System'}</p>
                {entry.notes && (
                    <p className="mt-2 text-xs text-ink-600 leading-relaxed rounded-lg bg-ink-50 px-3 py-2">{entry.notes}</p>
                )}
            </div>
        </li>
    );
}

function StageTile({ stage, state, onClick }) {
    const isCurrent = state === 'current';
    const isDone = state === 'done';

    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex items-center gap-1.5 rounded-lg px-2 py-1.5 min-w-0 transition-all text-left cursor-pointer hover:brightness-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal/50 ${
                isCurrent
                    ? 'bg-gradient-to-r from-brand-teal to-brand-teal-700 text-white shadow-sm shadow-brand-teal/20 ring-2 ring-brand-teal ring-offset-1 ring-offset-white'
                    : isDone
                        ? 'bg-brand-teal-50/90 ring-1 ring-brand-teal/20 hover:bg-brand-teal-50'
                        : 'bg-ink-50/50 ring-1 ring-ink-200/80 hover:bg-ink-100/60'
            }`}
            title={`${stage.label} — click to view history`}
        >
            <span
                className={`grid h-5 w-5 shrink-0 place-items-center rounded-md text-[10px] font-black tabular-nums ${
                    isCurrent
                        ? 'bg-white/20 text-white ring-1 ring-white/25'
                        : isDone
                            ? 'bg-brand-teal text-white'
                            : 'bg-white text-ink-400 ring-1 ring-ink-200'
                }`}
            >
                {isDone ? <Check className="h-3 w-3" /> : stage.order}
            </span>

            <span
                className={`flex-1 min-w-0 truncate text-[11px] font-semibold leading-none ${
                    isCurrent ? 'text-white' : isDone ? 'text-brand-teal-900' : 'text-ink-500'
                }`}
            >
                {stage.label}
            </span>

            {isCurrent && (
                <span className="shrink-0 rounded bg-brand-yellow/90 px-1 py-px text-[8px] font-black uppercase tracking-wide text-brand-teal-900">
                    Now
                </span>
            )}
        </button>
    );
}

function ModalShell({ title, children, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/50 backdrop-blur-sm" role="dialog" aria-modal="true">
            <div className="w-full max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-ink-200 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100">
                    <h3 className="font-heading font-extrabold text-ink-900">{title}</h3>
                    <button type="button" onClick={onClose} className="text-ink-400 hover:text-ink-800 text-xl leading-none" aria-label="Close">×</button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}
